/**
 * ChannelDiscoveryCard — S21
 *
 * Presentational card for a single listed channel in the discovery browse screen.
 *
 * HARD INVARIANTS (do not relax):
 *  - D-S48: NEVER renders a buy-credits button or link for ANY relationship
 *    (non-member, pending, OR approved). This module must not import any
 *    credit/purchase hook.
 *  - D-S22 (by omission): NEVER imports or calls useApproveAccessRequest /
 *    useDenyAccessRequest / change_member_role / remove_member / add_member.
 *  - B-2026-0105: template_count is intentionally 0 — NEVER render it.
 */

import { Link } from 'react-router-dom';
import type { ChannelPublicResponse } from '@/types/channel';
import type { ChannelRequestedRole } from '@/services/channelApi';
import { RequestToJoinModal } from '@/components/ui/RequestToJoinModal';
import { useState } from 'react';

export type ChannelRelationship = 'approved' | 'pending' | 'none';

interface ChannelDiscoveryCardProps {
  channel: ChannelPublicResponse;
  relationship: ChannelRelationship;
  /** Called when the user submits a join request. Resolves on success, throws on error. */
  onRequestJoin: (slug: string, message: string, role: ChannelRequestedRole) => Promise<void>;
}

/** Returns 1-2 uppercase initials from a display name. */
function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

/** Deterministic accent color from slug for logo fallback backgrounds. */
function getAccentColor(slug: string): string {
  const palette = [
    '#6366F1', // indigo
    '#8B5CF6', // violet
    '#EC4899', // pink
    '#F59E0B', // amber
    '#10B981', // emerald
    '#3B82F6', // blue
    '#EF4444', // red
    '#14B8A6', // teal
  ];
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 31 + slug.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length];
}

export function ChannelDiscoveryCard({
  channel,
  relationship,
  onRequestJoin,
}: ChannelDiscoveryCardProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const accentColor = getAccentColor(channel.slug);

  const taglineOrDesc =
    channel.tagline ??
    (channel.description
      ? channel.description.length > 120
        ? channel.description.slice(0, 117) + '…'
        : channel.description
      : null);

  const handleSubmit = async (message: string, role: ChannelRequestedRole) => {
    await onRequestJoin(channel.slug, message, role);
  };

  return (
    <>
      <article
        style={{
          backgroundColor: 'var(--color-dark-card)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '16px',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          transition: 'border-color 160ms ease, box-shadow 160ms ease',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-border-default, rgba(255,255,255,0.15))';
          (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 24px rgba(0,0,0,0.18)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-border-subtle)';
          (e.currentTarget as HTMLElement).style.boxShadow = 'none';
        }}
      >
        {/* Header row: logo + name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {/* Logo or initials fallback */}
          <div
            style={{
              flexShrink: 0,
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              overflow: 'hidden',
              backgroundColor: channel.school_logo_url ? 'transparent' : accentColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-hidden="true"
          >
            {channel.school_logo_url ? (
              <img
                src={channel.school_logo_url}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#fff',
                  fontFamily: 'var(--font-heading)',
                  letterSpacing: '-0.02em',
                }}
              >
                {getInitials(channel.display_name)}
              </span>
            )}
          </div>

          <div style={{ minWidth: 0, flex: 1 }}>
            <h3
              style={{
                margin: 0,
                fontSize: '15px',
                fontWeight: 700,
                fontFamily: 'var(--font-heading)',
                color: 'var(--color-text-primary)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {channel.display_name}
            </h3>

            {/* Member count — never template_count (B-2026-0105) */}
            <p
              style={{
                margin: '2px 0 0',
                fontSize: '12px',
                color: 'var(--color-text-muted)',
              }}
            >
              {channel.member_count === 1 ? '1 member' : `${channel.member_count} members`}
            </p>
          </div>
        </div>

        {/* Sport chip */}
        {channel.sport_focus && (
          <div>
            <span
              data-testid="sport-chip"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 10px',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                backgroundColor: `${accentColor}22`,
                color: accentColor,
                border: `1px solid ${accentColor}44`,
              }}
            >
              {channel.sport_focus}
            </span>
          </div>
        )}

        {/* Tagline / description */}
        {taglineOrDesc ? (
          <p
            style={{
              margin: 0,
              fontSize: '13px',
              lineHeight: '1.5',
              color: 'var(--color-text-secondary)',
              flex: 1,
            }}
          >
            {taglineOrDesc}
          </p>
        ) : (
          <p
            style={{
              margin: 0,
              fontSize: '13px',
              color: 'var(--color-text-muted)',
              fontStyle: 'italic',
              flex: 1,
            }}
          >
            No description provided.
          </p>
        )}

        {/* CTA — D-S48: gate strictly on relationship; NO buy affordance anywhere */}
        <div style={{ marginTop: 'auto', paddingTop: '4px' }}>
          {relationship === 'approved' && (
            <Link
              to={`/channels/${channel.slug}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                padding: '9px 20px',
                borderRadius: '10px',
                backgroundColor: 'var(--color-accent)',
                color: 'var(--color-text-inverse)',
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'var(--font-heading)',
                textDecoration: 'none',
                textAlign: 'center',
                transition: 'opacity 120ms ease',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = '0.88'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = '1'; }}
            >
              Open channel
            </Link>
          )}

          {relationship === 'pending' && (
            <button
              type="button"
              disabled
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                padding: '9px 20px',
                borderRadius: '10px',
                border: '1px solid var(--color-border-subtle)',
                backgroundColor: 'transparent',
                color: 'var(--color-text-muted)',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'not-allowed',
                opacity: 0.65,
              }}
            >
              Request pending
            </button>
          )}

          {relationship === 'none' && (
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                padding: '9px 20px',
                borderRadius: '10px',
                border: '1px solid var(--color-accent)',
                backgroundColor: 'transparent',
                color: 'var(--color-accent)',
                fontSize: '14px',
                fontWeight: 600,
                fontFamily: 'var(--font-heading)',
                cursor: 'pointer',
                transition: 'background-color 120ms ease, color 120ms ease',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--color-accent)';
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-inverse)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-accent)';
              }}
            >
              Request access
            </button>
          )}
        </div>
      </article>

      {/* Request-to-join modal — only rendered for non-members */}
      {relationship === 'none' && (
        <RequestToJoinModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleSubmit}
          channelName={channel.display_name}
        />
      )}
    </>
  );
}

export default ChannelDiscoveryCard;
